import React from 'react';
import { Link } from 'react-router-dom';
import '../styles/About.css';

const About = () => {
    const milestones = [
        {
            year: "2010",
            title: "Company Founded",
            description: "MapLube was established with a vision to provide high-quality lubricants to the Indian market."
        },
        {
            year: "2015",
            title: "ISO Certification",
            description: "Achieved ISO 9001:2015 certification for quality management systems."
        },
        {
            year: "2018",
            title: "API Certification",
            description: "Received API certification for engine oil standards and performance."
        },
        {
            year: "2020",
            title: "National Expansion",
            description: "Expanded operations to cover all major states across India."
        },
        {
            year: "2023",
            title: "Innovation Center",
            description: "Launched state-of-the-art R&D center for product development."
        }
    ];

    const certifications = [
        {
            name: "ISO 9001:2015",
            description: "Quality Management System",
            icon: "🏆"
        },
        {
            name: "API Certification",
            description: "Engine Oil Performance Standards",
            icon: "⚡"
        },
        {
            name: "BIS Certification",
            description: "Bureau of Indian Standards",
            icon: "🇮🇳"
        },
        {
            name: "OEM Approvals",
            description: "Original Equipment Manufacturer",
            icon: "🔧"
        }
    ];

    const downloadFiles = [
        {
            name: "MapLube TDS - Engine Oil 5W-30",
            description: "Technical Data Sheet for premium engine oil",
            size: "2.4 MB",
            type: "TXT",
            icon: "📄",
            file: "/documents/MapLube_TDS_Engine_Oil_5W-30.txt"
        },
        {
            name: "MapLube TDS - Two Wheeler Oil 20W-40",
            description: "Technical specifications for motorcycle oils",
            size: "1.8 MB",
            type: "TXT",
            icon: "📄",
            file: "/documents/MapLube_TDS_Two_Wheeler_Oil_20W-40.txt"
        },
        {
            name: "MapLube TDS - Commercial Vehicle Oil 15W-40",
            description: "Heavy-duty oil specifications",
            size: "2.1 MB",
            type: "TXT",
            icon: "📄",
            file: "/documents/MapLube_TDS_Commercial_Vehicle_Oil_15W-40.txt"
        },
        {
            name: "MapLube Product Catalog 2024",
            description: "Complete product range and specifications",
            size: "8.5 MB",
            type: "TXT",
            icon: "📚",
            file: "/documents/MapLube_Product_Catalog_2024.txt"
        }
    ];

    const contactInfo = [
        {
            title: "Sales Inquiries",
            description: "Get detailed quotes and product information",
            phone: "+91 95400 71804",
            email: "sales@maplube.in",
            icon: "📞"
        },
        {
            title: "Technical Support",
            description: "Expert guidance on product selection and usage",
            phone: "+91 95400 71804",
            email: "support@maplube.in",
            icon: "🔧"
        },
        {
            title: "Dealership Opportunities",
            description: "Join our network of authorized dealers",
            phone: "+91 95400 71804",
            email: "contact@maplube.in",
            icon: "🤝"
        },
        {
            title: "Customer Service",
            description: "General inquiries and support",
            phone: "+91 95400 71804",
            email: "support@maplube.in",
            icon: "💬"
        }
    ];

    return (
        <div className="about">
            {/* Hero Section */}
            <section className="about-hero">
                <div className="container">
                    <div className="about-hero-content">
                        <h1>About MapLube</h1>
                        <p>Leading the lubricant industry with innovation, quality, and trust since 2010</p>
                    </div>
                </div>
            </section>

            {/* Company Story */}
            <section className="company-story">
                <div className="container">
                    <div className="story-content">
                        <div className="story-text">
                            <h2>Our Story</h2>
                            <p>MapLube was founded in 2010 with a simple yet powerful mission: to provide high-quality lubricants that protect engines and maximize performance. What started as a small operation has grown into one of India's most trusted lubricant brands.</p>
                            <p>Our journey began with a deep understanding of the Indian market's unique needs. We recognized that vehicle owners and fleet operators needed reliable, high-performance lubricants that could withstand the diverse climatic conditions and demanding usage patterns across India.</p>
                            <p>Today, MapLube serves millions of customers across the country, from individual vehicle owners to large fleet operators, agricultural businesses, and industrial facilities. Our commitment to quality, innovation, and customer satisfaction remains at the core of everything we do.</p>
                            <div className="story-actions">
                                <Link to="/about-detailed" className="btn btn-primary">Learn More</Link>
                            </div>
                        </div>
                        <div className="story-image">
                            <img src="/logo/original_logo.jpeg" alt="MapLube Manufacturing" />
                        </div>
                    </div>
                </div>
            </section>

            {/* Mission & Vision */}
            <section className="mission-vision">
                <div className="container">
                    <div className="mission-vision-grid">
                        <div className="mission">
                            <div className="mission-icon">🎯</div>
                            <h3>Our Mission</h3>
                            <p>To provide innovative, high-quality lubricant solutions that enhance engine performance, extend equipment life, and contribute to sustainable operations while maintaining the highest standards of quality and customer satisfaction.</p>
                        </div>
                        <div className="vision">
                            <div className="vision-icon">🔮</div>
                            <h3>Our Vision</h3>
                            <p>To become the most trusted and preferred lubricant brand in India, known for innovation, quality, and commitment to customer success, while contributing to a sustainable future.</p>
                        </div>
                    </div>
                </div>
            </section>

            {/* Values */}
            <section className="values">
                <div className="container">
                    <h2>Our Core Values</h2>
                    <div className="values-grid">
                        <div className="value">
                            <div className="value-icon">🛡️</div>
                            <h3>Quality</h3>
                            <p>We never compromise on quality. Every product undergoes rigorous testing to meet international standards.</p>
                        </div>
                        <div className="value">
                            <div className="value-icon">💡</div>
                            <h3>Innovation</h3>
                            <p>We continuously invest in research and development to create cutting-edge lubricant solutions.</p>
                        </div>
                        <div className="value">
                            <div className="value-icon">🤝</div>
                            <h3>Trust</h3>
                            <p>Building long-term relationships based on transparency, reliability, and mutual success.</p>
                        </div>
                        <div className="value">
                            <div className="value-icon">🌱</div>
                            <h3>Sustainability</h3>
                            <p>Committed to environmental responsibility and sustainable business practices.</p>
                        </div>
                    </div>
                </div>
            </section>

            {/* Manufacturing Facility */}
            <section className="manufacturing">
                <div className="container">
                    <div className="manufacturing-content">
                        <div className="manufacturing-text">
                            <h2>State-of-the-Art Manufacturing</h2>
                            <p>Our manufacturing facility is equipped with the latest technology and automated systems to ensure consistent quality and efficiency. Located strategically in India, our facility features:</p>
                            <ul>
                                <li>Advanced blending and packaging equipment</li>
                                <li>Automated quality control systems</li>
                                <li>ISO-certified processes</li>
                                <li>Environmentally responsible operations</li>
                                <li>Capacity to meet growing market demands</li>
                            </ul>
                        </div>
                        <div className="manufacturing-image">
                            <img src="/logo/product.jpeg" alt="MapLube Manufacturing Facility" />
                        </div>
                    </div>
                </div>
            </section>

            {/* Certifications */}
            <section className="certifications">
                <div className="container">
                    <h2>Certifications & Standards</h2>
                    <div className="certifications-grid">
                        {certifications.map((cert, index) => (
                            <div key={index} className="certification-card">
                                <div className="certification-icon">{cert.icon}</div>
                                <h3>{cert.name}</h3>
                                <p>{cert.description}</p>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* Milestones */}
            <section className="milestones">
                <div className="container">
                    <h2>Our Journey</h2>
                    <div className="timeline">
                        {milestones.map((milestone, index) => (
                            <div key={index} className={`timeline-item ${index % 2 === 0 ? 'left' : 'right'}`}>
                                <div className="timeline-content">
                                    <div className="timeline-year">{milestone.year}</div>
                                    <h3>{milestone.title}</h3>
                                    <p>{milestone.description}</p>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* Statistics Section */}
            <section className="statistics">
                <div className="container">
                    <h2>MapLube by the Numbers</h2>
                    <div className="stats-grid">
                        <div className="stat-item">
                            <div className="stat-number">14+</div>
                            <div className="stat-label">Years of Excellence</div>
                        </div>
                        <div className="stat-item">
                            <div className="stat-number">50+</div>
                            <div className="stat-label">Product Variants</div>
                        </div>
                        <div className="stat-item">
                            <div className="stat-number">1000+</div>
                            <div className="stat-label">Authorized Dealers</div>
                        </div>
                        <div className="stat-item">
                            <div className="stat-number">1M+</div>
                            <div className="stat-label">Happy Customers</div>
                        </div>
                        <div className="stat-item">
                            <div className="stat-number">25+</div>
                            <div className="stat-label">States Covered</div>
                        </div>
                        <div className="stat-item">
                            <div className="stat-number">99.9%</div>
                            <div className="stat-label">Quality Assurance</div>
                        </div>
                    </div>
                </div>
            </section>

            {/* Team */}
            <section className="team">
                <div className="container">
                    <h2>Leadership Team</h2>
                    <div className="team-grid">
                        <div className="team-member">
                            <div className="member-avatar">👨‍💼</div>
                            {/* <h3>Ujjawal Sharma</h3> */}
                            <p className="member-role">Founder & CEO</p>
                            <p className="member-desc">With over 20 years of experience in the lubricant industry, he leads MapLube's strategic vision and growth. His leadership has been instrumental in establishing MapLube as a trusted brand across India.</p>
                        </div>
                        <div className="team-member">
                            <div className="member-avatar">👩‍🔬</div>
                            {/* <h3>Manoranjan Singh Manu</h3> */}
                            <p className="member-role">Head of R&D</p>
                            <p className="member-desc">A distinguished researcher with expertise in lubricant chemistry, he leads our innovation initiatives and ensures all products meet international quality standards.</p>
                        </div>
                        <div className="team-member">
                            <div className="member-avatar">👨‍💻</div>
                            {/*     <h3>Poonam Singh</h3> */}
                            <p className="member-role">Sales Director</p>
                            <p className="member-desc">He brings strategic sales expertise and has successfully expanded MapLube's market presence across multiple regions, building strong relationships with dealers and customers.</p>
                        </div>
                    </div>
                </div>
            </section>

            {/* Download TDS Section */}
            <section className="download-section">
                <div className="container">
                    <h2>Download Technical Data Sheets</h2>
                    <p className="section-description">Access detailed technical specifications and product information for all MapLube lubricants</p>
                    <div className="download-grid">
                        {downloadFiles.map((file, index) => (
                            <div key={index} className="download-card">
                                <div className="download-icon">{file.icon}</div>
                                <div className="download-content">
                                    <h3>{file.name}</h3>
                                    <p>{file.description}</p>
                                    <div className="file-info">
                                        <span className="file-size">{file.size}</span>
                                        <span className="file-type">{file.type}</span>
                                    </div>
                                </div>
                                <a href={file.file} download className="download-btn">Download</a>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* Contact Sales Section */}
            <section className="contact-sales">
                <div className="container">
                    <h2>Contact Sales Team</h2>
                    <p className="section-description">Get in touch with our expert sales team for personalized solutions and support</p>
                    <div className="contact-grid">
                        {contactInfo.map((contact, index) => (
                            <div key={index} className="contact-card">
                                <div className="contact-icon">{contact.icon}</div>
                                <div className="contact-content">
                                    <h3>{contact.title}</h3>
                                    <p>{contact.description}</p>
                                    <div className="contact-details">
                                        <div className="contact-item">
                                            <span className="contact-label">Phone:</span>
                                            <a href={`tel:${contact.phone}`} className="contact-link">{contact.phone}</a>
                                        </div>
                                        <div className="contact-item">
                                            <span className="contact-label">Email:</span>
                                            <a href={`mailto:${contact.email}`} className="contact-link">{contact.email}</a>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                    <div className="contact-cta">
                        <Link to="/contact" className="btn btn-primary">Contact Us Now</Link>
                        <Link to="/dealership" className="btn btn-secondary">Become a Dealer</Link>
                    </div>
                </div>
            </section>
        </div>
    );
};

export default About;
